import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "../../../lib/auth/hooks";
import { consumeOAuthReturnPath } from "../../../lib/auth/oauth";
import {
  prefetchReturnDestination,
  returnAfterAuth,
} from "../../../lib/auth/external-redirect";
import { startSignupOnboarding } from "../../../lib/auth/signup-onboarding";
import { Callout } from "../../../../ui/components/Callout";
import { AuthTransitionScreen } from "../../../../ui/components/AuthTransitionScreen";
import * as authPageStyles from "../../../../ui/components/AuthPageLayout/AuthPageLayout.style.css";
import { useI18n } from "../../../lib/i18n";

export interface GoogleCallbackPageProps {
  code?: string;
  error?: string;
}

export function GoogleCallbackPage({ code, error: oauthError }: GoogleCallbackPageProps) {
  const navigate = useNavigate();
  const { completeGoogleOAuth } = useAuth();
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Start warming the destination immediately — overlaps the code exchange.
    prefetchReturnDestination();

    if (oauthError) {
      setError(oauthError);
      return;
    }
    if (!code) {
      setError(t("account.googleCallback.missingCode"));
      return;
    }

    let cancelled = false;
    completeGoogleOAuth(code)
      .then((result) => {
        // Always finish navigation on success — StrictMode may have "cancelled"
        // this effect instance, but the OAuth exchange is deduped and must not
        // drop the handoff.
        if (result.isNewUser) {
          startSignupOnboarding();
          navigate({ to: "/user-setup", search: { tab: "phone" }, replace: true });
          return;
        }
        // Leave for the originating product app (or pro home) immediately —
        // before any internal navigation — so no product dashboard flashes.
        if (returnAfterAuth()) return;
        const returnTo = consumeOAuthReturnPath();
        navigate({
          to: returnTo === "/login" || returnTo === "/register" ? "/" : returnTo,
          replace: true,
        });
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : t("account.googleCallback.failed"));
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, oauthError, completeGoogleOAuth, navigate]);

  if (error) {
    return (
      <div className={authPageStyles.page} style={{ justifyContent: "center", padding: 24 }}>
        <Callout variant="error">{error}</Callout>
      </div>
    );
  }

  return <AuthTransitionScreen message={t("account.googleCallback.completing")} />;
}
